import * as React from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * Props for the TreeView component
 */
export interface TreeViewProps {
  /**
   * TreeView.Node components representing the tree structure
   */
  children: React.ReactNode;
  /**
   * Whether nodes can be selected
   * @default false
   */
  selectable?: boolean;
  /**
   * Array of selected node IDs (controlled)
   */
  selectedIds?: string[];
  /**
   * Callback when selection changes
   */
  onSelectionChange?: (ids: string[]) => void;
  /**
   * Whether multiple nodes can be selected
   * @default false
   */
  multiSelect?: boolean;
  /**
   * Additional CSS classes for the tree container
   */
  className?: string;
}

/**
 * Props for TreeView.Node component
 */
export interface TreeNodeProps {
  /**
   * Unique identifier for the node
   */
  id: string;
  /**
   * Label content displayed for the node
   */
  label: React.ReactNode;
  /**
   * Nested TreeView.Node components
   */
  children?: React.ReactNode;
  /**
   * Icon displayed before the label
   */
  icon?: React.ReactNode;
  /**
   * Whether the node is expanded by default
   * @default false
   */
  defaultExpanded?: boolean;
  /**
   * Whether the node is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Click handler for the node (in addition to selection)
   */
  onClick?: () => void;
}

/**
 * Node registration info for keyboard navigation
 */
interface NodeInfo {
  id: string;
  parentId?: string;
  hasChildren: boolean;
  isExpanded: boolean;
  isDisabled: boolean;
  element: HTMLElement | null;
}

/**
 * Context for passing tree state to compound components
 */
interface TreeViewContextValue {
  selectable: boolean;
  selectedIds: string[];
  multiSelect: boolean;
  onSelect: (id: string) => void;
  expandedIds: Set<string>;
  toggleExpanded: (id: string) => void;
  focusedId: string | null;
  setFocusedId: (id: string | null) => void;
  registerNode: (info: NodeInfo) => void;
  unregisterNode: (id: string) => void;
  getVisibleNodes: () => NodeInfo[];
  focusNode: (id: string) => void;
}

const TreeViewContext = React.createContext<TreeViewContextValue | null>(null);

function useTreeViewContext(): TreeViewContextValue {
  const context = React.useContext(TreeViewContext);
  if (!context) {
    throw new Error('TreeView compound components must be used within TreeView');
  }
  return context;
}

/**
 * Context for passing level info to nested nodes
 */
interface TreeNodeContextValue {
  level: number;
  parentId?: string;
}

const TreeNodeContext = React.createContext<TreeNodeContextValue>({
  level: 0,
  parentId: undefined,
});

/**
 * Helper to count React children that are TreeView.Node components
 */
function countTreeNodeChildren(children: React.ReactNode): number {
  return React.Children.toArray(children).filter(
    (child) => React.isValidElement(child) && (child.type as unknown as { displayName?: string })?.displayName === 'TreeView.Node'
  ).length;
}

/**
 * Navigation keys that are always allowed, even when disabled
 */
const NAVIGATION_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End']);

/**
 * Context for keyboard navigation handlers
 */
interface KeyboardNavigationContext {
  id: string;
  parentId?: string;
  hasChildren: boolean;
  isExpanded: boolean;
  selectable: boolean;
  visibleNodes: NodeInfo[];
  currentIndex: number;
  toggleExpanded: (id: string) => void;
  focusNode: (id: string) => void;
  onSelect: (id: string) => void;
  onClick?: () => void;
}

/**
 * Navigate to the next node (ArrowDown)
 */
function handleArrowDown(ctx: KeyboardNavigationContext): void {
  const nextIndex = ctx.currentIndex + 1;
  if (nextIndex < ctx.visibleNodes.length) {
    const nextNode = ctx.visibleNodes[nextIndex];
    /* v8 ignore start - defensive guard: array index always valid after bounds check */
    if (nextNode) {
      /* v8 ignore stop */
      ctx.focusNode(nextNode.id);
    }
  }
}

/**
 * Navigate to the previous node (ArrowUp)
 */
function handleArrowUp(ctx: KeyboardNavigationContext): void {
  const prevIndex = ctx.currentIndex - 1;
  if (prevIndex >= 0) {
    const prevNode = ctx.visibleNodes[prevIndex];
    /* v8 ignore start - defensive guard: array index always valid after bounds check */
    if (prevNode) {
      /* v8 ignore stop */
      ctx.focusNode(prevNode.id);
    }
  }
}

/**
 * Expand node or navigate to first child (ArrowRight)
 */
function handleArrowRight(ctx: KeyboardNavigationContext): void {
  if (!ctx.hasChildren) {
    return;
  }

  if (!ctx.isExpanded) {
    ctx.toggleExpanded(ctx.id);
    return;
  }

  // Navigate to first child
  const nextIndex = ctx.currentIndex + 1;
  if (nextIndex < ctx.visibleNodes.length) {
    const childNode = ctx.visibleNodes[nextIndex];
    if (childNode && childNode.parentId === ctx.id) {
      ctx.focusNode(childNode.id);
    }
  }
}

/**
 * Collapse node or navigate to parent (ArrowLeft)
 */
function handleArrowLeft(ctx: KeyboardNavigationContext): void {
  if (ctx.hasChildren && ctx.isExpanded) {
    ctx.toggleExpanded(ctx.id);
    return;
  }

  if (ctx.parentId) {
    ctx.focusNode(ctx.parentId);
  }
}

/**
 * Select or activate the node (Enter/Space)
 */
function handleActivate(ctx: KeyboardNavigationContext): void {
  if (ctx.onClick) {
    ctx.onClick();
  }
  if (ctx.selectable) {
    ctx.onSelect(ctx.id);
  }
}

/**
 * Navigate to first node (Home)
 */
function handleHome(ctx: KeyboardNavigationContext): void {
  const firstNode = ctx.visibleNodes[0];
  if (firstNode) {
    ctx.focusNode(firstNode.id);
  }
}

/**
 * Navigate to last node (End)
 */
function handleEnd(ctx: KeyboardNavigationContext): void {
  const lastNode = ctx.visibleNodes.at(-1);
  if (lastNode) {
    ctx.focusNode(lastNode.id);
  }
}

/**
 * Keyboard handler lookup map
 */
const keyboardHandlers: Record<string, (ctx: KeyboardNavigationContext) => void> = {
  ArrowDown: handleArrowDown,
  ArrowUp: handleArrowUp,
  ArrowRight: handleArrowRight,
  ArrowLeft: handleArrowLeft,
  Enter: handleActivate,
  ' ': handleActivate,
  Home: handleHome,
  End: handleEnd,
};

/**
 * TreeView.Node component - represents a single node in the tree
 */
const TreeNode = React.forwardRef<HTMLLIElement, TreeNodeProps>(
  (
    {
      id,
      label,
      children,
      icon,
      defaultExpanded = false,
      disabled = false,
      onClick,
    },
    ref
  ) => {
    const {
      selectable,
      selectedIds,
      onSelect,
      expandedIds,
      toggleExpanded,
      focusedId,
      setFocusedId,
      registerNode,
      unregisterNode,
      getVisibleNodes,
      focusNode,
    } = useTreeViewContext();

    const { level, parentId } = React.useContext(TreeNodeContext);
    const nodeRef = React.useRef<HTMLButtonElement>(null);
    const hasChildren = countTreeNodeChildren(children) > 0;

    // Memoize child context value (must be called unconditionally)
    const childContextValue = React.useMemo(
      () => ({ level: level + 1, parentId: id }),
      [level, id]
    );

    // Initialize expanded state from defaultExpanded
    const [initialized, setInitialized] = React.useState(false);
    React.useEffect(() => {
      if (!initialized && defaultExpanded && hasChildren) {
        toggleExpanded(id);
        setInitialized(true);
      }
    }, [initialized, defaultExpanded, hasChildren, id, toggleExpanded]);

    const isExpanded = expandedIds.has(id);
    const isSelected = selectedIds.includes(id);
    const isFocused = focusedId === id;

    // Register/unregister node for keyboard navigation
    React.useEffect(() => {
      registerNode({
        id,
        parentId,
        hasChildren,
        isExpanded,
        isDisabled: disabled,
        element: nodeRef.current,
      });
      return () => unregisterNode(id);
    }, [id, parentId, hasChildren, isExpanded, disabled, registerNode, unregisterNode]);

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled) return;

      setFocusedId(id);

      if (onClick) {
        onClick();
      }

      if (selectable) {
        onSelect(id);
      }
    };

    const handleExpandClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled) return;
      toggleExpanded(id);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      const isNavigationKey = NAVIGATION_KEYS.has(e.key);
      if (disabled && !isNavigationKey) {
        return;
      }

      const handler = keyboardHandlers[e.key];
      if (!handler) {
        return;
      }

      e.preventDefault();

      const visibleNodes = getVisibleNodes();
      const currentIndex = visibleNodes.findIndex((n) => n.id === id);

      const ctx: KeyboardNavigationContext = {
        id,
        parentId,
        hasChildren,
        isExpanded,
        selectable,
        visibleNodes,
        currentIndex,
        toggleExpanded,
        focusNode,
        onSelect,
        onClick,
      };

      handler(ctx);
    };

    const nodeClasses = [
      // Base styles
      'flex items-center',
      'h-[36px]',
      'px-2', // 8px horizontal padding
      'text-sm',
      'text-secondary-700',
      'cursor-pointer',
      'select-none',
      'transition-colors duration-150 ease-out',
      'outline-none',
      // Hover state
      !isSelected && !disabled && 'hover:bg-secondary-50',
      // Selected state
      isSelected && 'bg-primary-50 text-primary-700',
      // Focus state
      'focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-inset',
      // Disabled state
      disabled && 'opacity-50 cursor-not-allowed',
    ]
      .filter(Boolean)
      .join(' ');

    const expandIconClasses = [
      'w-4 h-4',
      'text-secondary-500', // neutral-500
      'transition-transform duration-150 ease-out',
      'flex-shrink-0',
      isExpanded && 'rotate-90',
    ]
      .filter(Boolean)
      .join(' ');

    // Indent based on level (24px per level)
    const indentStyle: React.CSSProperties = {
      paddingLeft: `${level * 24}px`,
    };

    return (
      <li ref={ref}>
        <button
          ref={nodeRef}
          type="button"
          role="treeitem"
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-selected={selectable ? isSelected : undefined}
          aria-level={level + 1}
          aria-disabled={disabled || undefined}
          className={nodeClasses}
          style={indentStyle}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          tabIndex={isFocused ? 0 : -1}
          data-node-id={id}
        >
          {/* Expand/collapse button */}
          <span
            className="w-5 h-5 flex items-center justify-center flex-shrink-0 mr-1"
            onClick={hasChildren ? handleExpandClick : undefined}
            aria-hidden="true"
          >
            {hasChildren && (
              <ChevronRight className={expandIconClasses} />
            )}
          </span>

          {/* Custom icon */}
          {icon && (
            <span className="flex-shrink-0 mr-2 flex items-center justify-center text-secondary-500">
              {icon}
            </span>
          )}

          {/* Label */}
          <span className="flex-1 min-w-0 truncate">{label}</span>
        </button>

        {/* Children */}
        {hasChildren && (
          <ul
            className={[
              'overflow-hidden',
              'transition-all duration-150 ease-out',
              isExpanded ? 'max-h-[9999px] opacity-100' : 'max-h-0 opacity-0',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <TreeNodeContext.Provider value={childContextValue}>
              {React.Children.map(children, (child) => {
                if (React.isValidElement(child) && (child.type as unknown as { displayName?: string })?.displayName === 'TreeView.Node') {
                  return child;
                }
                return null;
              })}
            </TreeNodeContext.Provider>
          </ul>
        )}
      </li>
    );
  }
);

TreeNode.displayName = 'TreeView.Node';

/**
 * TreeView component for displaying hierarchical data.
 *
 * Designed for insurance industry applications where hierarchical data
 * structures need clear visualization - policy hierarchies, organizational
 * charts, document trees, and nested navigation.
 *
 * Features:
 * - Expand/collapse nodes with smooth animation
 * - Single and multi-select modes
 * - Full keyboard navigation (arrows, Enter, Space, Home, End)
 * - Roving tabindex for proper focus management
 * - Accessible with proper ARIA attributes
 * - Compound component pattern (TreeView + TreeView.Node)
 *
 * @example
 * // Basic tree
 * <TreeView>
 *   <TreeView.Node id="1" label="Documents">
 *     <TreeView.Node id="1.1" label="Reports" />
 *     <TreeView.Node id="1.2" label="Contracts">
 *       <TreeView.Node id="1.2.1" label="2024" />
 *       <TreeView.Node id="1.2.2" label="2023" />
 *     </TreeView.Node>
 *   </TreeView.Node>
 *   <TreeView.Node id="2" label="Settings" />
 * </TreeView>
 *
 * @example
 * // Tree with selection
 * <TreeView
 *   selectable
 *   selectedIds={selectedIds}
 *   onSelectionChange={setSelectedIds}
 * >
 *   <TreeView.Node id="policies" label="Policies" icon={<FileText />}>
 *     <TreeView.Node id="auto" label="Auto Insurance" />
 *     <TreeView.Node id="home" label="Home Insurance" />
 *   </TreeView.Node>
 * </TreeView>
 *
 * @example
 * // Tree with multi-select
 * <TreeView
 *   selectable
 *   multiSelect
 *   selectedIds={selectedIds}
 *   onSelectionChange={setSelectedIds}
 * >
 *   <TreeView.Node id="1" label="Item 1" />
 *   <TreeView.Node id="2" label="Item 2" />
 *   <TreeView.Node id="3" label="Item 3" />
 * </TreeView>
 */
const TreeViewRoot = React.forwardRef<HTMLDivElement, TreeViewProps>(
  (
    {
      children,
      selectable = false,
      selectedIds: controlledSelectedIds,
      onSelectionChange,
      multiSelect = false,
      className = '',
    },
    ref
  ) => {
    // Internal state for uncontrolled selection
    const [internalSelectedIds, setInternalSelectedIds] = React.useState<string[]>([]);

    // Expanded state (always internal)
    const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());

    // Focus state
    const [focusedId, setFocusedId] = React.useState<string | null>(null);

    // Node registry for keyboard navigation
    const nodesRef = React.useRef<Map<string, NodeInfo>>(new Map());

    // Determine if controlled
    const isControlled = controlledSelectedIds !== undefined;
    const selectedIds = isControlled ? controlledSelectedIds : internalSelectedIds;

    const handleSelect = React.useCallback(
      (id: string) => {
        let newSelection: string[];

        if (multiSelect) {
          if (selectedIds.includes(id)) {
            newSelection = selectedIds.filter((selectedId) => selectedId !== id);
          } else {
            newSelection = [...selectedIds, id];
          }
        } else {
          newSelection = selectedIds.includes(id) ? [] : [id];
        }

        if (!isControlled) {
          setInternalSelectedIds(newSelection);
        }
        onSelectionChange?.(newSelection);
      },
      [isControlled, multiSelect, onSelectionChange, selectedIds]
    );

    const toggleExpanded = React.useCallback((id: string) => {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    }, []);

    const registerNode = React.useCallback((info: NodeInfo) => {
      nodesRef.current.set(info.id, info);
    }, []);

    const unregisterNode = React.useCallback((id: string) => {
      nodesRef.current.delete(id);
    }, []);

    // Get visible nodes in DOM order for keyboard navigation
    const getVisibleNodes = React.useCallback((): NodeInfo[] => {
      const treeElement = ref && 'current' in ref ? ref.current : null;
      if (!treeElement) {
        // Fallback: return nodes from registry (may not be in correct order)
        return Array.from(nodesRef.current.values()).filter((node) => {
          // Check if all ancestors are expanded
          let currentId = node.parentId;
          while (currentId) {
            if (!expandedIds.has(currentId)) {
              return false;
            }
            const parent = nodesRef.current.get(currentId);
            currentId = parent?.parentId;
          }
          return true;
        });
      }

      // Get nodes in DOM order
      const nodeElements = treeElement.querySelectorAll('[data-node-id]');
      const visibleNodes: NodeInfo[] = [];

      nodeElements.forEach((el) => {
        const id = (el as HTMLElement).dataset.nodeId;
        if (id) {
          const info = nodesRef.current.get(id);
          if (info) {
            // Check if visible (all ancestors expanded)
            let isVisible = true;
            let currentId = info.parentId;
            while (currentId) {
              if (!expandedIds.has(currentId)) {
                isVisible = false;
                break;
              }
              const parent = nodesRef.current.get(currentId);
              currentId = parent?.parentId;
            }
            if (isVisible) {
              visibleNodes.push({ ...info, element: el as HTMLElement });
            }
          }
        }
      });

      return visibleNodes;
    }, [expandedIds, ref]);

    const focusNode = React.useCallback((id: string) => {
      setFocusedId(id);
      const node = nodesRef.current.get(id);
      if (node?.element) {
        node.element.focus();
      } else {
        // Try to find and focus the element
        const element = document.querySelector(`[data-node-id="${id}"]`) as HTMLElement;
        if (element) {
          element.focus();
        }
      }
    }, []);

    // Initialize focus on first node if none focused
    React.useEffect(() => {
      if (focusedId === null) {
        const visibleNodes = getVisibleNodes();
        const firstNode = visibleNodes[0];
        if (firstNode) {
          setFocusedId(firstNode.id);
        }
      }
    }, [focusedId, getVisibleNodes]);

    const contextValue: TreeViewContextValue = React.useMemo(() => ({
      selectable, selectedIds, multiSelect, onSelect: handleSelect,
      expandedIds, toggleExpanded, focusedId, setFocusedId,
      registerNode, unregisterNode, getVisibleNodes, focusNode,
    }), [
      selectable, selectedIds, multiSelect, handleSelect,
      expandedIds, toggleExpanded, focusedId, setFocusedId,
      registerNode, unregisterNode, getVisibleNodes, focusNode,
    ]);

    const treeClasses = ['', className].filter(Boolean).join(' ');

    return (
      <TreeViewContext.Provider value={contextValue}>
        <div ref={ref} role="tree" className={treeClasses}>
          <TreeNodeContext.Provider value={React.useMemo(() => ({ level: 0, parentId: undefined }), [])}>
            {children}
          </TreeNodeContext.Provider>
        </div>
      </TreeViewContext.Provider>
    );
  }
);

TreeViewRoot.displayName = 'TreeView';

/**
 * TreeView compound component with Node
 */
export const TreeView = Object.assign(TreeViewRoot, {
  Node: TreeNode,
});
